import React, { useCallback, useState } from "react";
import type {
  AgendaConsultant,
  AgendaDayPayload,
  AgendaFiltersState,
  AgendaWeekSummary,
} from "../agendaTypes";
import type { AgendaViewport } from "../agendaBreakpoints";
import AgendaHeader from "./AgendaHeader";
import AgendaScheduleCard from "./AgendaScheduleCard";
import AgendaToolbar from "./AgendaToolbar";
import KpiCards from "./KpiCards";
import WeatherAlert from "./WeatherAlert";
import type { ScheduleSearchItem } from "../../../types/scheduleCard";
import { AGENDA_FEATURE_FLAGS } from "../constants/agendaFeatureFlags";
import type { AgendaWeatherAlert } from "../agendaWeatherTypes";
import { useAgendaWeatherContext } from "../context/AgendaWeatherContext";
import { AgendaGridSkeleton } from "./AgendaSkeleton";

type DesktopTabletAgendaViewProps = {
  viewport: AgendaViewport;
  filters: AgendaFiltersState;
  dayData: AgendaDayPayload;
  weekSummary: AgendaWeekSummary;
  loadingDay: boolean;
  consultants: AgendaConsultant[];
  consultantOptions: AgendaConsultant[];
  cityOptions: string[];
  showCityFilter: boolean;
  showConsultantFilter: boolean;
  canManageSchedules: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  searchLoading: boolean;
  searchResults: ScheduleSearchItem[];
  onOpenSearchResult: (item: ScheduleSearchItem) => void;
  onNewSchedule: () => void;
  onBulkBlock: () => void;
  onApplyFilters: (patch: Partial<AgendaFiltersState>) => void;
  dateSelected: string[];
  initialStatus: number | undefined;
  onSelectCell: (
    scheduleOption: { id: number; date: string; status: number },
    event: React.MouseEvent
  ) => void;
  onSelectCellMouseOver: (coord: string, event: React.MouseEvent) => void;
  onOpenVisitDetails: (coord: string) => void;
  detailPanel: React.ReactNode;
};

const DesktopTabletAgendaView: React.FC<DesktopTabletAgendaViewProps> = ({
  viewport,
  filters,
  dayData,
  weekSummary,
  loadingDay,
  consultants,
  consultantOptions,
  cityOptions,
  showCityFilter,
  showConsultantFilter,
  canManageSchedules,
  search,
  onSearchChange,
  searchLoading,
  searchResults,
  onOpenSearchResult,
  onNewSchedule,
  onBulkBlock,
  onApplyFilters,
  dateSelected,
  initialStatus,
  onSelectCell,
  onSelectCellMouseOver,
  onOpenVisitDetails,
  detailPanel,
}) => {
  const isDesktop = viewport === "desktop";
  const isTablet = viewport === "tablet";
  const hasDetailPanel = Boolean(detailPanel);
  const [highlightedScheduleIds, setHighlightedScheduleIds] = useState<
    Set<number>
  >(() => new Set());

  const agendaWeather = useAgendaWeatherContext();
  const weatherAlertsForViewer = agendaWeather.data?.alerts ?? [];

  const handleViewAffectedVisits = useCallback((alert: AgendaWeatherAlert) => {
    setHighlightedScheduleIds(new Set(alert.scheduleIds));
    const targetId = `agenda-city-${alert.cityName
      .replace(/\s+/g, "-")
      .toLowerCase()}`;
    document.getElementById(targetId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  return (
    <div className="w-full">
      <AgendaHeader
        selectedDateIso={filters.date}
        search={search}
        onSearchChange={onSearchChange}
        searchLoading={searchLoading}
        searchResults={searchResults}
        onOpenSearchResult={onOpenSearchResult}
        onNewSchedule={onNewSchedule}
        onBulkBlock={onBulkBlock}
        canManageSchedules={canManageSchedules}
        layout={isTablet ? "tablet" : "desktop"}
      />

      {isTablet ? (
        <div className="mb-4 flex min-w-0 flex-nowrap items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <AgendaToolbar
            filters={filters}
            weekSummary={weekSummary}
            cityOptions={cityOptions}
            consultants={consultantOptions}
            showCityFilter={showCityFilter}
            showConsultantFilter={showConsultantFilter}
            onApplyFilters={onApplyFilters}
            viewport={viewport}
            className="mb-0 min-w-0 shrink-0"
          />
          <KpiCards
            kpis={dayData.kpis}
            compact
            className="mb-0 shrink-0 whitespace-nowrap text-xs"
          />
        </div>
      ) : (
        <>
          <KpiCards kpis={dayData.kpis} compact={!isDesktop} />
          <AgendaToolbar
            filters={filters}
            weekSummary={weekSummary}
            cityOptions={cityOptions}
            consultants={consultantOptions}
            showCityFilter={showCityFilter}
            showConsultantFilter={showConsultantFilter}
            onApplyFilters={onApplyFilters}
            viewport={viewport}
          />
        </>
      )}


      {AGENDA_FEATURE_FLAGS.rainAlerts &&
      weatherAlertsForViewer.length > 0 &&
      viewport !== "mobile" ? (
        <WeatherAlert
          alerts={weatherAlertsForViewer}
          onViewAffected={handleViewAffectedVisits}
        />
      ) : null}

      {loadingDay ? (
        <AgendaGridSkeleton className="min-w-0" />
      ) : !dayData.has_open_hours ? (
        <div className="rounded-lg border border-dashed border-[#E2E8F0] bg-white px-6 py-8 text-center text-sm text-[#718096]">
          Nenhum horário aberto para este dia
        </div>
      ) : (
        <AgendaScheduleCard
          className="min-w-0"
          detailPanelOpen={hasDetailPanel}
          dateIso={filters.date}
          dayData={dayData}
          consultants={consultants}
          cityWeatherByName={agendaWeather.cityByName}
          weatherCities={agendaWeather.data?.cities ?? []}
          weatherPreferredCity={agendaWeather.location?.city}
          weatherCommercialRegionId={agendaWeather.location?.commercialRegionId}
          hourRainByCity={agendaWeather.hourRainByCity}
          weatherLoading={agendaWeather.loading}
          weatherFailed={agendaWeather.error}
          weatherAttribution={agendaWeather.data?.attribution ?? null}
          highlightedScheduleIds={highlightedScheduleIds}
          canManageSchedules={canManageSchedules}
          initialStatus={initialStatus}
          dateSelected={dateSelected}
          onSelectCell={onSelectCell}
          onSelectCellMouseOver={onSelectCellMouseOver}
          onOpenVisitDetails={onOpenVisitDetails}
          showLegend={viewport !== "mobile"}
          legendPlacement={isTablet ? "bottom" : "top"}
        />
      )}

      {detailPanel}
    </div>
  );
};

export default DesktopTabletAgendaView;
